import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Skeleton,
  Fab,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  ClickAwayListener,
  IconButton,
  Chip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EmailIcon from '@mui/icons-material/Email'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import ColorPicker from '../components/ColorPicker'
import type { TagDTO, AddTagInput, UpdateTagInput, StudentDTO } from '@school-homestead/shared'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SortColumn = 'name' | 'description'
type SortDirection = 'asc' | 'desc'

interface ColumnDef {
  id: SortColumn
  label: string
  editable: boolean
  sortable: boolean
}

const COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Name', editable: true, sortable: true },
  { id: 'description', label: 'Description', editable: true, sortable: true },
]

const EMPTY_FORM: AddTagInput = {
  name: '',
  description: '',
  color: '#1976d2',
}

// ---------------------------------------------------------------------------
// Inline Edit Cell
// ---------------------------------------------------------------------------

interface InlineEditCellProps {
  value: string
  tagId: number
  column: SortColumn
  onSave: (tagId: number, field: SortColumn, value: string) => void
}

function InlineEditCell({ value, tagId, column, onSave }: InlineEditCellProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  function handleClick() {
    setDraft(value)
    setEditing(true)
  }

  function handleSave() {
    setEditing(false)
    if (draft.trim() !== value.trim()) {
      onSave(tagId, column, draft)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setEditing(false)
      setDraft(value)
    }
  }

  if (!editing) {
    return (
      <Typography
        variant="body2"
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          '&:hover': { bgcolor: 'action.hover' },
          minHeight: 24,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {value || '—'}
      </Typography>
    )
  }

  return (
    <ClickAwayListener onClickAway={handleSave}>
      <Box>
        <TextField
          size="small"
          variant="standard"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          sx={{
            '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.25 },
            minWidth: 100,
          }}
        />
      </Box>
    </ClickAwayListener>
  )
}

// ---------------------------------------------------------------------------
// Inline Color Edit Cell
// ---------------------------------------------------------------------------

interface InlineColorCellProps {
  color: string
  tagId: number
  onSave: (tagId: number, color: string) => void
}

function InlineColorCell({ color, tagId, onSave }: InlineColorCellProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(color)

  function handleClick() {
    setDraft(color)
    setEditing(true)
  }

  function handleSave() {
    setEditing(false)
    if (draft.trim() !== color.trim() && /^#[0-9a-fA-F]{6}$/.test(draft.trim())) {
      onSave(tagId, draft.trim())
    }
  }

  if (!editing) {
    return (
      <Box
        onClick={handleClick}
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        title={color}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: color,
            border: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        />
      </Box>
    )
  }

  return (
    <ClickAwayListener onClickAway={handleSave}>
      <Box sx={{ minWidth: 220 }}>
        <ColorPicker value={draft} onChange={setDraft} />
      </Box>
    </ClickAwayListener>
  )
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export default function TagsPage() {
  const queryClient = useQueryClient()

  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Add dialog state
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState<AddTagInput>(EMPTY_FORM)

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<TagDTO | null>(null)

  // Students (needed for email BCC by tag)
  const { data: students } = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      const res = await api.get<StudentDTO[]>('/api/students')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    staleTime: 60_000,
  })

  // Data
  const { data, isLoading, error } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const res = await api.get<TagDTO[]>('/api/tags')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
  })

  // Add mutation
  const addMutation = useMutation({
    mutationFn: async (input: AddTagInput) => {
      const res = await api.post<TagDTO>('/api/tags', input)
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      setAddOpen(false)
      setForm(EMPTY_FORM)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, input }: { id: number; input: UpdateTagInput }) => {
      const res = await api.patch<TagDTO>(`/api/tags/${id}`, input)
      if (!res.success) throw new Error(res.error)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      // Also invalidate students so tag pills update with new name/color
      queryClient.invalidateQueries({ queryKey: ['students'] })
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.delete<null>(`/api/tags/${id}`)
      if (!res.success) throw new Error(res.error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['students'] })
      setDeleteTarget(null)
    },
  })

  // Handlers
  function handleSort(col: SortColumn) {
    if (sortColumn === col) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(col)
      setSortDirection('asc')
    }
  }

  function handleInlineSave(tagId: number, field: SortColumn, value: string) {
    const payload: UpdateTagInput = {}
    if (field === 'name') payload.name = value
    if (field === 'description') payload.description = value
    updateMutation.mutate({ id: tagId, input: payload })
  }

  function handleColorSave(tagId: number, color: string) {
    updateMutation.mutate({ id: tagId, input: { color } })
  }

  function handleAddClose() {
    setAddOpen(false)
    setForm(EMPTY_FORM)
    addMutation.reset()
  }

  /** Collect all student emails for a tag and open an Outlook-compatible mailto BCC link. */
  function handleEmailTag(tagId: number) {
    const emails: string[] = []
    if (students) {
      for (const s of students) {
        if (s.contactEmail && s.tags.some((t) => t.id === tagId)) {
          emails.push(s.contactEmail)
        }
      }
    }
    if (emails.length === 0) return
    const a = document.createElement('a')
    a.href = `mailto:?bcc=${emails.join(',')}`
    a.click()
  }

  const isFormValid = form.name.trim().length > 0

  // Sort tags client-side
  const tags = useMemo(() => {
    const raw = data ?? []
    return [...raw].sort((a, b) => {
      let aVal: string
      let bVal: string

      switch (sortColumn) {
        case 'name':
          aVal = a.name.toLowerCase()
          bVal = b.name.toLowerCase()
          break
        case 'description':
          aVal = a.description.toLowerCase()
          bVal = b.description.toLowerCase()
          break
        default:
          return 0
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [data, sortColumn, sortDirection])

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Tags{' '}
          {!isLoading && tags.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
              ({tags.length})
            </Typography>
          )}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          Add Tag
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load tags.
        </Alert>
      )}

      {/* Inline update error */}
      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(updateMutation.error as Error).message}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48, fontWeight: 700 }} />
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{ fontWeight: 700 }}
                  sortDirection={sortColumn === col.id ? sortDirection : false}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortColumn === col.id}
                      direction={sortColumn === col.id ? sortDirection : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 700 }}>Color</TableCell>
              <TableCell sx={{ width: 48, fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ width: 48, fontWeight: 700 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? [1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="circular" width={24} height={24} />
                    </TableCell>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.id}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                    <TableCell>
                      <Skeleton variant="circular" width={20} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="circular" width={24} height={24} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="circular" width={24} height={24} />
                    </TableCell>
                  </TableRow>
                ))
              : tags.map((tag) => (
                  <TableRow key={tag.id} hover>
                    <TableCell sx={{ width: 48, p: 1 }}>
                      <Chip
                        icon={<LocalOfferIcon />}
                        label={tag.name}
                        size="small"
                        sx={{
                          bgcolor: tag.color,
                          color: '#fff',
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: '#fff' },
                        }}
                      />
                    </TableCell>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.id}>
                        {col.editable ? (
                          <InlineEditCell
                            value={col.id === 'name' ? tag.name : tag.description}
                            tagId={tag.id}
                            column={col.id}
                            onSave={handleInlineSave}
                          />
                        ) : (
                          <Typography variant="body2">
                            {col.id === 'name' ? tag.name : tag.description}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      <InlineColorCell color={tag.color} tagId={tag.id} onSave={handleColorSave} />
                    </TableCell>
                    <TableCell sx={{ width: 48, p: 1 }}>
                      <Tooltip title="Email all students with this tag">
                        <IconButton
                          size="small"
                          onClick={() => handleEmailTag(tag.id)}
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: 48, p: 1 }}>
                      <Tooltip title="Delete tag">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(tag)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

            {!isLoading && tags.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No tags yet. Create your first tag to start labeling students.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mobile FAB */}
      <Tooltip title="Add Tag">
        <Fab
          color="primary"
          onClick={() => setAddOpen(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, display: { xs: 'flex', sm: 'none' } }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Add Tag Dialog */}
      <Dialog open={addOpen} onClose={handleAddClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tag</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Description"
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
            />
            <ColorPicker
              value={form.color ?? '#1976d2'}
              onChange={(color) => setForm({ ...form, color })}
            />
            {addMutation.isError && (
              <Alert severity="error">{(addMutation.error as Error).message}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => addMutation.mutate(form)}
            disabled={!isFormValid || addMutation.isPending}
          >
            {addMutation.isPending ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={700}>Delete Tag?</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete the tag <strong>{deleteTarget?.name}</strong>? This will remove it
            from all students who currently have it assigned.
          </Typography>
          {deleteMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(deleteMutation.error as Error).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}